import axios, { type AxiosInstance } from 'axios';
import { LocalStorageManager } from '../utils/LocalStorageManager';

// Use environment variable for API base URL, with a fallback for development
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5050/api';

const baseApiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
baseApiClient.interceptors.request.use((config) => {
  const token = LocalStorageManager.getAuthToken(); 
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Handle global errors, especially 401
baseApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle errors globally
    console.error('API call error:', error);
    if (error.response?.status === 401) {
      LocalStorageManager.nuke();
      // Handle unauthorized errors, redirect to home/auth page
      window.location.pathname = '/';
    }
    return Promise.reject(error);
  }
);

// Extended API client with additional helper methods
export const apiClient = {
  ...baseApiClient,
  
  /**
   * POST request with FormData (for file uploads)
   */
  async postFormData<T>(url: string, formData: FormData): Promise<T> {
    const response = await baseApiClient.post<T>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * GET request with query params
   */
  async get<T>(url: string, params?: Record<string, any>): Promise<T> {
    const response = await baseApiClient.get<T>(url, { params });
    return response.data;
  },

  /**
   * POST request returning data
   */
  async post<T>(url: string, data?: any): Promise<T> {
    const response = await baseApiClient.post<T>(url, data);
    return response.data;
  },

  /**
   * DELETE request with data in body
   */
  async delete<T>(url: string, data?: any): Promise<T> {
    const response = await baseApiClient.delete<T>(url, { data });
    return response.data;
  },
  
  /**
   * DELETE request with query params
   */
  async deleteWithParams<T>(url: string, params?: Record<string, any>): Promise<T> {
    const response = await baseApiClient.delete<T>(url, { params });
    return response.data;
  }
};
